import React, { useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';

function ImageUpload({ userData }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadedUrl, setUploadedUrl] = useState(null);
    const fileInputRef = useRef(null);

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (!selected) return;

        setFile(selected);
        setUploadedUrl(null);
        setPreview(URL.createObjectURL(selected));
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const dropped = e.dataTransfer.files[0];
        if (!dropped) return;

        setFile(dropped);
        setUploadedUrl(null);
        setPreview(URL.createObjectURL(dropped));
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error('Please select a file first');
            return;
        }

        const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowed.includes(file.type)) {
            toast.error('Only JPEG, PNG, GIF and WebP files are allowed');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('File must be under 5MB');
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('image', file);

            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/';
            const response = await fetch(`${apiUrl}upload`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            const data = await response.json();

            if (data.url) {
                setUploadedUrl(data.url);
                toast.success('Image uploaded successfully');
            } else {
                toast.error(data.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleCopy = () => {
        if (!uploadedUrl) return;
        navigator.clipboard.writeText(uploadedUrl);
        toast.success('URL copied to clipboard');
    };

    const handleReset = () => {
        setFile(null);
        setPreview(null);
        setUploadedUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <AdminLayout>
            <ContentBox theme="default" tint="lightskyblue" header="Image Upload">
                <div className="space-y-4">
                    {!preview ? (
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            onDrop={handleDrop}
                            onDragOver={(e) => e.preventDefault()}
                            className="w-full border-2 border-dashed border-gray-400 rounded bg-gray-50 hover:bg-gray-100 transition-colors py-10 flex flex-col items-center gap-3"
                        >
                            <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                            </svg>
                            <span className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] text-gray-600">
                                Click or drag and drop to upload
                            </span>
                            <span className="text-xs text-gray-400">JPEG, PNG, GIF or WebP, max 5MB</span>
                        </button>
                    ) : (
                        <div className="space-y-3">
                            <div className="border-2 border-gray-300 rounded overflow-hidden bg-gray-100 flex items-center justify-center max-h-64">
                                <img src={preview} alt="Preview" className="max-w-full max-h-64 object-contain" />
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600 truncate max-w-[60%]">{file?.name}</span>
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    className="text-xs text-gray-500 hover:text-red-600 font-['Jersey_10'] uppercase tracking-wide transition-colors"
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleFileChange}
                        className="hidden"
                    />

                    {file && !uploadedUrl && (
                        <button
                            onClick={handleUpload}
                            disabled={uploading}
                            className="w-full font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:bg-red-900 transition-colors rounded disabled:opacity-50"
                            style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                        >
                            {uploading ? 'Uploading...' : 'Upload Image'}
                        </button>
                    )}

                    {uploadedUrl && (
                        <div className="space-y-3">
                            <div className="bg-green-50 border border-green-300 rounded p-3">
                                <p className="text-xs text-green-700 font-['Jersey_10'] uppercase tracking-wide mb-2">
                                    ✓ Uploaded successfully
                                </p>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        readOnly
                                        value={uploadedUrl}
                                        className="flex-1 text-xs bg-white border border-green-300 rounded px-2 py-1.5 text-gray-700 font-mono"
                                    />
                                    <button
                                        onClick={handleCopy}
                                        className="font-['Jersey_10'] uppercase text-[12px] tracking-[1px] px-3 py-1.5 bg-green-600 text-white hover:bg-green-700 rounded transition-colors whitespace-nowrap"
                                    >
                                        Copy URL
                                    </button>
                                </div>
                            </div>
                            <button
                                onClick={handleReset}
                                className="w-full font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors rounded"
                                style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                            >
                                Upload Another
                            </button>
                        </div>
                    )}
                </div>
            </ContentBox>
        </AdminLayout>
    );
}

export default ImageUpload;